/**
 * The workspace shell — ADR-015's deliberately DUMB layout engine: it reads
 * the store's current layout, builds zone/tab chrome, and mounts each visible
 * panel through the registry. Panels receive a container and the store —
 * never their location (§3: docking/floats/splits later are engine upgrades
 * touching zero panels). The engine's one hard obligation (Codex P2 on the
 * ADR): every mounted panel that leaves the visible layout is DISPOSED —
 * tab-away, swap, close, mode switch — so no hidden panel stays subscribed
 * to the store and re-opening never stacks subscribers.
 * Depends on: panel-registry, workspace (types + transitions), store.
 * Depended on by: main (wiring only).
 */
import type { PanelHandle, PanelRegistry } from './panel-registry.js';
import { closeTier2 } from './tooltip.js';
import type { PanelId } from './panel-ids.js';
import type { AppStore } from '../session/store.js';
import {
  activateTab,
  isZoneCollapsed,
  setZoneCollapsed,
  ZONE_NAMES,
  type WorkspaceLayout,
  type ZoneName,
} from './workspace.js';

export interface WorkspaceShell {
  /** Live mount evidence: which panels are mounted right now (probe surface). */
  mountedPanels(): readonly PanelId[];
  dispose(): void;
}

interface Mounted {
  readonly panel: PanelId;
  readonly handle: PanelHandle;
  readonly element: HTMLElement;
}

/**
 * Mount the engine around the viewport center. `root` is the flex row/column
 * scaffold from index.html: zones are created here, the center is left alone.
 */
export function mountWorkspaceShell(
  root: { readonly left: HTMLElement; readonly right: HTMLElement; readonly bottom: HTMLElement },
  store: AppStore,
  registry: PanelRegistry,
): WorkspaceShell {
  const zoneHosts: Record<ZoneName, HTMLElement> = { left: root.left, right: root.right, bottom: root.bottom };
  let mounted: Mounted[] = [];
  let renderedLayout: WorkspaceLayout | null = null;

  const disposeAll = (): void => {
    for (const m of mounted) m.handle.dispose(); // the ADR's engine obligation
    mounted = [];
    // The tier-2 popover is a body-level singleton whose ? button lives in a
    // panel: a remount detaches the owner and strands the popover open at
    // stale coordinates (Codex P2, #78) — it goes down with its panels.
    closeTier2();
  };

  // The collapse affordance (Allen, 2026-08-01): a zone folds to a slim RAIL
  // that names its panels — nothing closes, nothing is forgotten, and the
  // rail is the door back in. Chevrons point where the zone will go.
  const COLLAPSE_GLYPH: Record<ZoneName, string> = { left: '‹', right: '›', bottom: '⌄' };
  const EXPAND_GLYPH: Record<ZoneName, string> = { left: '›', right: '‹', bottom: '⌃' };

  const render = (): void => {
    const layout = store.getLayout();
    if (layout === renderedLayout) return; // value identity — nothing moved
    renderedLayout = layout;
    disposeAll();
    for (const zone of ZONE_NAMES) {
      const host = zoneHosts[zone];
      host.replaceChildren();
      host.classList.remove('zone-collapsed');
      const groups = layout.zones[zone];
      host.style.display = groups.length === 0 ? 'none' : 'flex';
      if (groups.length === 0) {
        host.style.flexBasis = '';
        continue;
      }
      if (isZoneCollapsed(layout, zone)) {
        // Collapsed: a rail, no mounts — a hidden panel is a DISPOSED panel
        // (the engine obligation), so nothing stays subscribed off-stage.
        host.classList.add('zone-collapsed');
        host.style.flexBasis = '';
        const expand = document.createElement('button');
        expand.type = 'button';
        expand.className = 'zone-toggle';
        expand.textContent = EXPAND_GLYPH[zone];
        expand.setAttribute('aria-label', `expand ${zone} panels`);
        expand.addEventListener('click', () => {
          store.setLayout(setZoneCollapsed(store.getLayout(), zone, false));
        });
        host.appendChild(expand);
        const rail = document.createElement('div');
        rail.className = 'zone-rail';
        groups.forEach((group, groupIndex) => {
          for (const placement of group.panels) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.textContent = registry.get(placement.id).title;
            chip.setAttribute('aria-label', `open ${registry.get(placement.id).title}`);
            chip.addEventListener('click', () => {
              // Expand AND surface the named panel — the rail chip is a door,
              // not a label.
              const current = store.getLayout();
              store.setLayout(
                setZoneCollapsed(activateTab(current, zone, groupIndex, placement.id), zone, false),
              );
            });
            rail.appendChild(chip);
          }
        });
        host.appendChild(rail);
        continue;
      }
      // The zone's extent across its stacking axis is its WIDEST tenant, not
      // the sum — groups stack along the zone now, so summing made a
      // two-tenant shelf eat half the screen (Allen, 2026-08-01: persisted
      // pre-tab layouts carry 0.22+0.28 in the right zone). Sizes still set
      // the groups' relative shares along the stacking axis; a side shelf is
      // additionally capped at 0.35 — no shelf owns half the stage.
      const widest = Math.max(...groups.map((group) => group.size));
      const cap = zone === 'bottom' ? 0.9 : 0.35;
      host.style.flexBasis = `${Math.round(Math.min(widest, cap) * 100)}%`;
      const chrome = document.createElement('div');
      chrome.className = 'zone-chrome';
      const collapse = document.createElement('button');
      collapse.type = 'button';
      collapse.className = 'zone-toggle';
      collapse.textContent = COLLAPSE_GLYPH[zone];
      collapse.setAttribute('aria-label', `collapse ${zone} panels`);
      collapse.addEventListener('click', () => {
        store.setLayout(setZoneCollapsed(store.getLayout(), zone, true));
      });
      chrome.appendChild(collapse);
      host.appendChild(chrome);
      const groupsEl = document.createElement('div');
      groupsEl.className = 'zone-groups';
      host.appendChild(groupsEl);
      groups.forEach((group, groupIndex) => {
        const groupEl = document.createElement('section');
        groupEl.className = 'panel-group';
        groupEl.style.flexBasis = `${Math.round(group.size * 100)}%`;
        // Every group names itself (Allen, 2026-08-01 — and the arm-space
        // findability lesson): a lone panel gets a title block; a stack gets
        // its tab bar, which IS the title with something to choose between.
        if (group.panels.length === 1) {
          const title = document.createElement('div');
          title.className = 'panel-title';
          title.textContent = registry.get(group.active).title;
          groupEl.appendChild(title);
        }
        if (group.panels.length > 1) {
          const tabs = document.createElement('div');
          tabs.className = 'panel-tabs';
          tabs.setAttribute('role', 'tablist');
          for (const placement of group.panels) {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.setAttribute('role', 'tab');
            tab.setAttribute('aria-selected', String(placement.id === group.active));
            tab.textContent = registry.get(placement.id).title;
            tab.addEventListener('click', () => {
              store.setLayout(activateTab(store.getLayout(), zone, groupIndex, placement.id));
            });
            tabs.appendChild(tab);
          }
          groupEl.appendChild(tabs);
        }
        const body = document.createElement('div');
        body.className = 'panel-body';
        body.dataset['panel'] = group.active;
        body.dataset['register'] = group.panels.find(p=>p.id===group.active)?.register ?? layout.register;
        groupEl.appendChild(body);
        groupsEl.appendChild(groupEl);
        const handle = registry.get(group.active).mount(body, store);
        mounted.push({ panel: group.active, handle, element: body });
      });
    }
  };

  const unsubscribe = store.subscribe(render);
  render();

  return {
    mountedPanels: () => mounted.map((m) => m.panel),
    dispose() {
      unsubscribe();
      disposeAll();
    },
  };
}
